<template>
  <div class="mobile-admin-page">
    <div class="nav-bar-operations">
      <img
        src="@/assets/logogruas.png"
        alt="HomePage"
        class="logo-button"
        @click="backToModule"
      />
    </div>

    <div class="page-shell">
      <div class="card-strip">
        <div class="text-operations">
          <h2>Instalaciones de la aplicación móvil</h2>
        </div>
      </div>

      <div class="action-buttons">
        <button class="action-button" @click="backToModule">Volver al menú</button>
        <button
          @click="fetchMobileAppInstalls"
          class="action-button action-primary"
          :disabled="mobileAppInstallsLoading"
        >
          {{ mobileAppInstallsLoading ? 'Actualizando…' : 'Actualizar lista' }}
        </button>
      </div>

      <p v-if="mobileAppInstallsError" class="text-danger" style="margin: 8px 12px 12px;">
        {{ mobileAppInstallsError }}
      </p>

      <div class="table-container card-wrap">
        <h2>Instalaciones registradas</h2>
        <p style="margin: 0 0 12px; font-size: 13px; color: #666;">
          Cada instalación tiene un ID único en el dispositivo (Ajustes). Asigna una unidad
          desde aquí para empujar el cambio a la app. Una unidad solo puede estar vinculada
          a una instalación a la vez.
        </p>
        <table class="table">
          <thead>
            <tr>
              <th>ID instalación</th>
              <th>Unidad</th>
              <th>Último operador</th>
              <th>Última actividad</th>
              <th>Push</th>
              <th style="min-width: 260px;">Acciones</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="!mobileAppInstallsLoading && mobileAppInstalls.length === 0">
              <td colspan="6" style="text-align: center; color: #888;">
                No hay instalaciones registradas. Aparecen cuando un operador abre la app e inicia sesión.
              </td>
            </tr>
            <tr v-for="install in mobileAppInstalls" :key="install.install_id">
              <td>
                <code style="font-size: 11px;">{{ install.install_id }}</code>
              </td>
              <td>
                <strong v-if="install.unit_short_id">{{ install.unit_short_id }}</strong>
                <span v-else style="color: #999;">Sin unidad</span>
                <div v-if="install.unit?.plates" style="font-size: 11px; color: #666;">
                  {{ install.unit.plates }}
                </div>
              </td>
              <td>
                <span v-if="install.last_operator_name">{{ install.last_operator_name }}</span>
                <span v-else style="color: #999;">—</span>
                <div v-if="install.last_operator_ident" style="font-size: 11px; color: #666;">
                  {{ install.last_operator_ident }}
                </div>
              </td>
              <td>{{ formatMobileAppTimestamp(install.last_seen_at) }}</td>
              <td>
                <span v-if="install.has_push_token" style="color: #2e7d32;">Sí</span>
                <span v-else style="color: #999;">No</span>
              </td>
              <td>
                <div style="display: flex; gap: 6px; align-items: center; flex-wrap: wrap;">
                  <input
                    v-model="mobileAppAssignDrafts[install.install_id]"
                    type="text"
                    placeholder="Ej. U-14"
                    style="width: 90px; padding: 4px 8px; font-size: 13px;"
                    :disabled="mobileAppActionInstallId === install.install_id"
                  />
                  <button
                    class="action-button action-primary"
                    style="padding: 4px 10px; font-size: 12px;"
                    :disabled="mobileAppActionInstallId === install.install_id"
                    @click="assignMobileAppInstall(install)"
                  >
                    Asignar
                  </button>
                  <button
                    class="action-button"
                    style="padding: 4px 10px; font-size: 12px;"
                    :disabled="!install.unit_short_id || mobileAppActionInstallId === install.install_id"
                    @click="unassignMobileAppInstall(install)"
                  >
                    Desvincular
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import Swal from 'sweetalert2';

export default {
  name: 'MobileInstallsAdmin',
  data() {
    return {
      permissions: [],
      isSuperuser: false,
      mobileAppInstalls: [],
      mobileAppInstallsLoading: false,
      mobileAppInstallsError: '',
      mobileAppAssignDrafts: {},
      mobileAppActionInstallId: null,
    };
  },
  computed: {
    canManageMobileApp() {
      return this.hasPerm('access_control.manage_mobile_app') || this.hasPerm('manage_mobile_app');
    },
  },
  created() {
    this.loadPermissions();
    this.fetchCurrentUser();
    this.fetchMobileAppInstalls();
  },
  methods: {
    loadPermissions() {
      try {
        const stored = localStorage.getItem('userPermissions');
        this.permissions = stored ? JSON.parse(stored) : [];
      } catch (error) {
        this.permissions = [];
      }
    },
    hasPerm(perm) {
      if (this.isSuperuser) return true;
      if (!this.permissions || this.permissions.length === 0) return false;
      return this.permissions.includes(perm) || this.permissions.includes(`access_control.${perm}`);
    },
    async fetchCurrentUser() {
      const apiDirectory = process.env.API_DIRECTORY;
      try {
        const { data } = await axios.get(`${apiDirectory}/current-user/`, {
          headers: { Authorization: 'Token ' + localStorage.getItem('authToken') },
        });
        this.permissions = data.permissions || this.permissions;
        this.isSuperuser = !!data.is_superuser;
        if (!this.canManageMobileApp) {
          this.$router.push({ name: 'MainPanel' });
        }
      } catch (error) {
        this.$router.push({ name: 'MainPanel' });
      }
    },
    backToModule() {
      this.$router.push({ path: '/modulo-movil' });
    },
    formatMobileAppTimestamp(value) {
      if (!value) return '—';
      try {
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return value;
        return date.toLocaleString('es-MX');
      } catch (error) {
        return value;
      }
    },
    async fetchMobileAppInstalls() {
      const apiDirectory = process.env.API_DIRECTORY;
      this.mobileAppInstallsLoading = true;
      this.mobileAppInstallsError = '';
      try {
        const response = await axios.get(
          `${apiDirectory}/mobile-app-installs/`,
          { headers: { Authorization: 'Token ' + localStorage.getItem('authToken') } }
        );
        const results = Array.isArray(response.data?.results) ? response.data.results : [];
        this.mobileAppInstalls = results;
        const drafts = { ...this.mobileAppAssignDrafts };
        results.forEach((install) => {
          if (drafts[install.install_id] === undefined) {
            drafts[install.install_id] = install.unit_short_id || '';
          }
        });
        this.mobileAppAssignDrafts = drafts;
      } catch (error) {
        console.error('Error fetching mobile app installs:', error);
        this.mobileAppInstallsError =
          error?.response?.data?.detail
          || error?.response?.data?.error
          || 'No se pudo cargar la lista de instalaciones.';
      } finally {
        this.mobileAppInstallsLoading = false;
      }
    },
    async assignMobileAppInstall(install) {
      if (!install?.install_id) return;
      const unitShortId = (this.mobileAppAssignDrafts[install.install_id] || '').trim();
      if (!unitShortId) {
        Swal.fire({
          title: 'Unidad requerida',
          text: 'Escribe el identificador de la unidad (ej. U-14).',
          icon: 'warning',
          confirmButtonText: 'Ok',
        });
        return;
      }

      const apiDirectory = process.env.API_DIRECTORY;
      this.mobileAppActionInstallId = install.install_id;
      try {
        await axios.post(
          `${apiDirectory}/mobile-app-installs/assign/`,
          { install_id: install.install_id, unit_short_id: unitShortId },
          { headers: { Authorization: 'Token ' + localStorage.getItem('authToken') } }
        );
        await this.fetchMobileAppInstalls();
        Swal.fire({
          title: 'Unidad asignada',
          text: `La unidad ${unitShortId} quedó vinculada a esta instalación.`,
          icon: 'success',
          timer: 1800,
          showConfirmButton: false,
        });
      } catch (error) {
        const data = error?.response?.data || {};
        Swal.fire({
          title: 'No se pudo asignar',
          text: data.detail || data.error || 'Error al asignar la unidad.',
          icon: 'error',
          confirmButtonText: 'Ok',
        });
      } finally {
        this.mobileAppActionInstallId = null;
      }
    },
    async unassignMobileAppInstall(install) {
      if (!install?.install_id) return;
      const confirm = await Swal.fire({
        title: 'Desvincular unidad',
        text: install.unit_short_id
          ? `¿Quitar ${install.unit_short_id} de esta instalación?`
          : '¿Quitar la unidad de esta instalación?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Desvincular',
        cancelButtonText: 'Cancelar',
      });
      if (!confirm.isConfirmed) return;

      const apiDirectory = process.env.API_DIRECTORY;
      this.mobileAppActionInstallId = install.install_id;
      try {
        await axios.post(
          `${apiDirectory}/mobile-app-installs/unassign/`,
          { install_id: install.install_id },
          { headers: { Authorization: 'Token ' + localStorage.getItem('authToken') } }
        );
        this.mobileAppAssignDrafts = {
          ...this.mobileAppAssignDrafts,
          [install.install_id]: '',
        };
        await this.fetchMobileAppInstalls();
        Swal.fire({
          title: 'Unidad desvinculada',
          icon: 'success',
          timer: 1400,
          showConfirmButton: false,
        });
      } catch (error) {
        const data = error?.response?.data || {};
        Swal.fire({
          title: 'No se pudo desvincular',
          text: data.detail || data.error || 'Error al desvincular la unidad.',
          icon: 'error',
          confirmButtonText: 'Ok',
        });
      } finally {
        this.mobileAppActionInstallId = null;
      }
    },
  },
};
</script>

<style scoped src="@/styles/mobile-admin.css"></style>
